import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/outline';
import { useSwipeable } from 'react-swipeable';

function CarouselScreen({ slides, bgColor = "bg-violet-900", logo, logoAlt, showBackButton, dashboardPath }) {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  const goToSlide = (index) => {
    if (!mounted) return;
    if (index < 0) {
      setCurrentSlide(slides.length - 1);
    } else if (index >= slides.length) {
      setCurrentSlide(0);
    } else {
      setCurrentSlide(index);
    }
  };

  const handlers = useSwipeable({
    onSwipedLeft: () => goToSlide(currentSlide + 1),
    onSwipedRight: () => goToSlide(currentSlide - 1),
    preventDefaultTouchmoveEvent: true,
    trackMouse: false,
    trackTouch: true,
    delta: 10,
    swipeDuration: 250
  });

  // Handle CTA button click with the callback
  const handleCtaClick = async () => {
    const currentSlideData = slides[currentSlide];
    
    // Call the onClickCta callback if it exists
    if (currentSlideData.onClickCta && typeof currentSlideData.onClickCta === 'function') {
      try {
        await currentSlideData.onClickCta();
      } catch (error) {
        console.error('Error executing slide callback:', error);
      }
    }
    
    // Then navigate to the specified route
    navigate(currentSlideData.route);
  };


  if (!mounted) return null;

  return (
    <div className={`min-h-screen ${bgColor} md:flex md:items-start pt-8 md:pt-40`}>
      <div className="relative w-full md:w-2/3 min-h-screen md:min-h-0 mx-auto px-6 py-8">
        {/* Desktop navigation buttons - hidden on mobile */}
        {currentSlide > 0 && (
          <button 
            className="hidden md:block absolute left-2 top-1/2 -translate-y-1/2 p-2 text-white hover:bg-violet-800 rounded-full"
            aria-label="Previous slide"
            onClick={() => goToSlide(currentSlide - 1)}
          >
            <ChevronLeftIcon className="w-8 h-8" />
          </button>
        )}
        
        {currentSlide < slides.length - 1 && (
          <button 
            className="hidden md:block absolute right-2 top-1/2 -translate-y-1/2 p-2 text-white hover:bg-violet-800 rounded-full"
            aria-label="Next slide"
            onClick={() => goToSlide(currentSlide + 1)}
          >
            <ChevronRightIcon className="w-8 h-8" />
          </button>
        )}

        {/* Main content */}
        <div {...handlers} className="flex-1 flex flex-col h-full">
          {/* Logo - fixed at top */}
          {logo && (
            <div className="flex items-center justify-center mb-4">
              <img 
                src={logo} 
                alt={logoAlt || "Logo"}
                className="w-20 h-20 md:w-48 md:h-48 object-contain"
              />
            </div>
          )}

          {/* Scrollable content section — aria-live announces slide changes */}
          <div className="flex-1 flex flex-col items-center">
            <div
              aria-live="polite"
              aria-atomic="true"
              className="w-full md:w-2/3 flex flex-col text-center text-white space-y-6 mb-8 overflow-y-auto"
            >
              <h1 className="text-4xl font-bold">
                {slides[currentSlide].title}
              </h1>
              
              {slides[currentSlide].content.map((paragraph, index) => (
                <p 
                  key={index} 
                  className="leading-relaxed md:text-xl"
                  dangerouslySetInnerHTML={{ __html: paragraph }}
                />
              ))}

              {slides[currentSlide].footnoteText && (
                <p className="text-sm md:text-lg italic">
                  {slides[currentSlide].footnoteText}
                </p>
              )}
            </div>
          </div>

          {/* Progress dots and button */}
          <div className="mt-auto">
            <div role="tablist" aria-label="Slide progress" className="flex justify-center items-center space-x-2 mb-8">
              {slides.map((slide, index) => (
                <div 
                  key={index}
                  role="tab"
                  aria-selected={index === currentSlide}
                  aria-label={`Slide ${index + 1} of ${slides.length}: ${slide.title}`}
                  className={`w-2 h-2 rounded-full bg-white ${
                    index === currentSlide ? 'opacity-100' : 'opacity-50'
                  }`}
                ></div>
              ))}
            </div>

            <div className="flex flex-col items-center space-y-4">
            <button
              onClick={handleCtaClick}
              aria-label={slides[currentSlide].ctaText}
              className="w-full md:max-w-md md:mx-auto py-4 bg-violet-600 text-white rounded-lg text-lg font-medium hover:bg-violet-500 transition-colors"
            >
              {slides[currentSlide].ctaText}
            </button>
            {showBackButton && (
              <Link 
                to={dashboardPath || slides[0]?.route || '/patient-dashboard'}
                className="text-white/80 hover:text-white text-sm font-medium transition-colors"
              >
                ... or go back to Dashboard
              </Link>
            )}
          </div>

          </div>
        </div>
      </div>
    </div>
  );
}

export default CarouselScreen;